import moment from 'moment'
import { forwardRef, useEffect, useState } from 'react'
import WithReports from './WithReports'
import { formateDate, startEndTruncate, walletLink } from 'utils'
import { map, join, startCase, truncate } from 'lodash'
import classNames from 'classnames'
import { Tooltip } from 'antd'
import { IoCopyOutline } from 'react-icons/io5'
import notify from 'components/atoms/Notify'
import queryString from 'query-string'
import BookingLogsDrawer from 'pages/booking/bookingLogs'
import TransactionLogsDrawer from 'pages/transaction/transactionLogs'
import useModal from 'hooks/useModal'
import UpdateTransactionStatusModal from 'pages/transaction/transactionModal'
import { useTranslation } from 'react-i18next'
import apiGetBlob from 'lib/network/apiGetBlob'
import { formatPrice } from 'lib/utils'
import { getAjarleeServiceFee, getInsuranceDisplay, getOfficeDeliveryAmount } from 'pages/booking/bookingDetailHelpers'

// eslint-disable-next-line
const TransactionLogs = forwardRef(({ paymentMethod, status, ...props }, ref) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [transactionLogs, setTransactionLogs] = useState([])
  const [selectedTransactionForStatusUpdate, setSelectedTransactionForStatusUpdate] = useState(null)
  const { t } = useTranslation()

  const handleStatusClick = transaction => {
    setSelectedTransactionForStatusUpdate(transaction)
    openUpdateStatusModal()
  }

  const handleStatusUpdateSuccess = updatedTransaction => {
    notify.success(t('transactions.messages.transactionStatusUpdated'))
    window.location.reload()
  }

  const [UpdateStatusModal, openUpdateStatusModal, closeUpdateStatusModal] = useModal({
    content: <UpdateTransactionStatusModal transaction={selectedTransactionForStatusUpdate} onSuccess={handleStatusUpdateSuccess} />,
    title: t('transactions.modals.updateTransactionStatus'),
    closeable: true,
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const handleInvoiceDownload = async bookingId => {
    if (!bookingId) return

    try {
      const blob = await apiGetBlob(`bookings/${bookingId}/invoice/download`)

      if (!blob) {
        return
      }

      const blobUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `booking_${bookingId}_invoice.pdf`
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading invoice:', error)
    }
  }

  const copyText = text => {
    navigator.clipboard.writeText(text).then(() => {
      notify.success(t('transactions.messages.copiedToClipboard'))
    })
  }

  const calculateAjarleeAmount = (totalPrice, percentage) => {
    if (!totalPrice || !percentage) return 0
    return formatPrice(totalPrice * (percentage / 100))
  }

  const calculateCompanyAmount = (totalPrice, percentage) => {
    if (!totalPrice || !percentage) return totalPrice || 0
    return formatPrice(totalPrice - totalPrice * (percentage / 100))
  }

  useEffect(() => {
    const qs = queryString.parse(window.location.search)

    if (qs?.bookingId) {
      setSelectedBooking({ _id: qs?.bookingId })
      setIsDrawerOpen(true)
    }
  }, [])

  const columns = [
    {
      name: t('transactions.columns.thawaniInvoiceId'),
      width: '10%',
      key: 'stripe',
      sortable: false,
      rendered: item => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{item?.transactionId}</span>
          {item?.transactionId && (
            <button id="copy-transaction-id" className="hover:text-indigo-500" onClick={() => copyText(item.transactionId)}>
              <IoCopyOutline className="ml-2" />
            </button>
          )}
        </div>
      ),
    },
    {
      name: t('transactions.columns.transactionStatus'),
      width: '10%',
      key: 'status',
      sortable: false,
      rendered: item => (
        <span
          className={`text-xs font-regular ${
            item.status === 'completed'
              ? 'bg-green-100 text-green-700'
              : item.status === 'canceled'
              ? 'bg-red-100 text-red-700'
              : item.status === 'initiated'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-gray-200'
          } px-2 py-1 rounded-md`}
        >
          {t(`transactions.status.${item.status}`)}
        </span>
      ),
    },
    {
      name: t('transactions.columns.bookingInvoice'),
      width: '10%',
      key: 'status',
      sortable: false,
      rendered: item => <div className="text-xs text-blue-500 font-mono">{item?.bookingInvoice}</div>,
    },
    {
      name: t('transactions.columns.booking'),
      width: '10%',
      key: 'type',
      sortable: false,
      rendered: item => (
        <div className="text-xs text-blue-500 font-mono">
          {item?.booking ? (
            <Tooltip
              color="white"
              overlayClassName="!min-w-[400px] !w-full"
              title={
                <div className="flex flex-col gap-2 p-2">
                  <div className="flex border rounded p-2 flex-col text-[12px] text-gray-500">
                    <b className="mb-1.5 border-b">{t('transactions.tooltips.carDetails')}</b>
                    <div className="text-xs font-mono">
                      {item?.booking ? (
                        <>
                          <div>
                            {t('transactions.tooltips.name')}{' '}
                            <a target="_blank" className="text-blue-500" href={`/car/edit/${item?.booking?.rentalCar?._id}/car-profile`} rel="noreferrer">
                              {item?.booking?.car?.make?.make || ''} {item?.booking?.car?.model?.modal || ''} {item?.booking?.rentalCar?.modelYear || ''}
                            </a>
                          </div>
                          <div className="mt-1">
                            {t('transactions.tooltips.status')} {item?.booking?.rentalCar?.status}
                          </div>
                          <div className="mt-1">
                            {t('transactions.tooltips.company')}{' '}
                            <a target="_blank" className="text-blue-500" href={`/company/edit/${item?.booking?.rentalCar?.company?._id}/company-profile`} rel="noreferrer">
                              {item?.booking?.rentalCar?.company?.name}
                            </a>
                          </div>
                          <div className="mt-1">
                            {t('transactions.tooltips.branch')} {item?.booking?.branch?.name}
                          </div>
                        </>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                  <div className="flex border rounded p-2 flex-col text-[12px] text-gray-500">
                    <b className="mb-1.5 border-b">{t('transactions.tooltips.bookingDetails')}</b>
                    <div className="text-xs font-mono">
                      {item?.booking ? (
                        <>
                          <div>
                            {t('transactions.tooltips.status')}{' '}
                            <span
                              className={classNames(`text-xs font-medium p-0.5 rounded-md`, {
                                'bg-gray-200 text-black': item?.booking?.status === 'pending',
                                'text-white bg-dark-600': item?.booking?.status === 'pending',
                                'bg-rose-400 !text-white': item?.booking?.status === 'cancelled',
                                'bg-green-500 !text-white': item?.booking?.status === 'completed' || item?.booking?.status === 'confirmed',
                              })}
                            >
                              {t(`transactions.bookingStatus.${item?.booking?.status}`)}
                            </span>
                          </div>
                          <div className="mt-1">
                            {t('transactions.tooltips.startDate')} <span className="text-black">{formateDate(moment(item?.booking?.rentalStartDate))}</span>
                          </div>
                          <div className="mt-1">
                            {t('transactions.tooltips.endDate')} <span className="text-black">{formateDate(moment(item?.booking?.rentalEndDate))}</span>
                          </div>
                        </>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                  {item?.booking?.delivery && (
                    <div className="flex border rounded p-2 flex-col text-[12px] text-gray-500">
                      <b className="mb-1.5 border-b">{t('transactions.tooltips.deliveryDetails')}</b>
                      <div className="text-xs font-mono">
                        <div className="mt-1">
                          {t('transactions.tooltips.type')} {item?.booking?.delivery?.type}
                        </div>
                        <div className="mt-1">
                          {t('transactions.tooltips.pickupAddress')} {item?.booking?.delivery?.pickupLocationAddress || item?.booking?.address || 'Office'}
                        </div>
                        <div className="mt-1">
                          {t('transactions.tooltips.dropOffAddress')} {item?.booking?.delivery?.dropOffLocationAddress || item?.booking?.address || 'Office'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              }
            >
              <a target="_blank" href={`/booking/${item?.booking?._id}`} rel="noreferrer">
                {t('transactions.buttons.viewBooking')}
              </a>
            </Tooltip>
          ) : (
            ''
          )}
        </div>
      ),
    },
    {
      name: t('transactions.columns.invoice'),
      width: '10%',
      key: 'name',
      sortable: true,
      rendered: item => {
        if (item?.booking?.invoiceUrl) {
          return (
            <div>
              <button
                onClick={() => handleInvoiceDownload(item?.booking?._id || item?.booking?.id)}
                className="text-sm text-normal text-blue-400 hover:text-blue-600 underline cursor-pointer"
              >
                {t('transactions.buttons.downloadInvoice')}
              </button>
            </div>
          )
        }
      },
    },
    {
      name: t('transactions.columns.user'),
      width: '10%',
      key: 'type',
      sortable: false,
      rendered: item => (
        <div className="text-xs text-blue-500 font-mono">
          {item?.user ? (
            <Tooltip
              color="white"
              overlayClassName="!min-w-[400px] !w-full"
              title={
                <div className="flex flex-col gap-2 p-2">
                  <div className="flex border rounded p-2 flex-col text-[12px] text-gray-500">
                    <b className="mb-1.5 border-b">{t('transactions.tooltips.userDetails')}</b>
                    <div className="text-xs font-mono">
                      {item?.user ? (
                        <>
                          <div>
                            {t('transactions.tooltips.name')}{' '}
                            <a target="_blank" className="text-blue-500" href={`/users/edit/${item?.user?._id}`} rel="noreferrer">
                              {item?.user?.fullName}
                            </a>
                          </div>
                          <div className="mt-1">
                            {t('transactions.tooltips.email')} <span className="text-black">{item?.user?.email}</span>{' '}
                          </div>
                          <div className="mt-1">
                            {t('transactions.tooltips.phone')} <span className="text-black">{item?.user?.phone}</span>{' '}
                          </div>
                          <div className="mt-1">
                            {t('transactions.tooltips.nationality')} <span className="text-black">{item?.user?.nationality}</span>{' '}
                          </div>
                        </>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                </div>
              }
            >
              <a target="_blank" className="text-blue-500" href={`/users/edit/${item?.user?._id}`} rel="noreferrer">
                {item?.user?.fullName || item?.user?.email || item?.user?.phone}
              </a>
            </Tooltip>
          ) : (
            ''
          )}
        </div>
      ),
    },
    {
      name: t('transactions.columns.amount'),
      width: '10%',
      key: 'amount',
      sortable: false,
      rendered: item => (
        <Tooltip
          color="white"
          overlayClassName="!min-w-[270px] !w-full"
          title={
            <div className="flex flex-col gap-2">
              <div className="flex border rounded p-2 flex-col text-[12px] text-gray-500">
                <b className="mb-1.5 border-b">{t('transactions.tooltips.paymentBreakdown')}</b>
                <div className="text-xs font-mono">
                  {item?.booking ? (
                    <>
                      <div>
                        <div className="mt-1 grid grid-cols-2">
                          {t('transactions.tooltips.carPrice')} <span className="text-black">{formatPrice(item?.booking?.priceWithoutTax || 0, true)} OMR</span>
                        </div>
                        {item?.booking?.priceWithoutTax !== item?.booking?.rentalCarDiscountPrice && (
                          <div className="mt-1 grid grid-cols-2">
                            {t('transactions.tooltips.discountedPrice')} <span className="text-black">{formatPrice(item?.booking?.rentalCarDiscountPrice || 0, true)} OMR</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-1 grid grid-cols-2">
                        {t('transactions.tooltips.depositAmount')} <span className="text-black">{formatPrice(item?.booking?.depositAmount || 0, true)} OMR</span>{' '}
                      </div>
                      <div className="mt-1 grid grid-cols-2">
                        {t('transactions.tooltips.deliveryFee')} <span className="text-black">{formatPrice(getOfficeDeliveryAmount(item?.booking), true)} OMR</span>{' '}
                      </div>
                      <div className="mt-1 grid grid-cols-2">
                        {t('transactions.tooltips.serviceFee')} <span className="text-black">{formatPrice(getAjarleeServiceFee(item?.booking), true)} OMR</span>{' '}
                      </div>
                      <div className="mt-1 grid grid-cols-2">
                        {t('transactions.tooltips.insuranceFee')} <span className="text-black">{formatPrice(getInsuranceDisplay(item?.booking).displayAmount, true)} OMR</span>{' '}
                      </div>
                      <div className="mt-1 mb-2 grid grid-cols-2">
                        {t('transactions.tooltips.vatTax')} <span className="text-black">{formatPrice(item?.booking?.vatTax || 0, true)} OMR</span>{' '}
                      </div>
                      <div className="mt-1 grid border-t pt-1 grid-cols-2">
                        {t('transactions.tooltips.totalAmount')} <span className="text-black">{formatPrice(Number(item?.booking?.totalPrice) || 0, true)} OMR</span>{' '}
                      </div>

                      <div className="mt-1 grid pt-1 grid-cols-2">
                        Ajarlee {item?.booking?.ajarleePercentage}%:{' '}
                        <span className="text-black">
                          {formatPrice(Number(calculateAjarleeAmount(item?.booking?.totalPrice, item?.booking?.ajarleePercentage)) || 0, true)} OMR
                        </span>
                      </div>

                      <div className="mt-1 grid border-t pt-1 grid-cols-2">
                        {t('transactions.tooltips.companyAmount')}{' '}
                        <span className="text-black">
                          {formatPrice(Number(calculateCompanyAmount(item?.booking?.totalPrice, item?.booking?.ajarleePercentage)) || 0, true)} OMR
                        </span>
                      </div>
                    </>
                  ) : (
                    t('transactions.tooltips.noPaymentDetails')
                  )}
                </div>
              </div>
            </div>
          }
        >
          <span className={`text-xs font-medium text-gray-500 py-1 rounded-md`}>{formatPrice(item.amount, true)} OMR</span>
        </Tooltip>
      ),
    },
    {
      name: t('transactions.columns.paymentMethod'),
      width: '10%',
      key: 'paymentMethod',
      sortable: false,
      rendered: item => {
        return <div className="text-xs text-blue-500 font-mono">{item?.paymentMethod}</div>
      },
    },
    {
      name: t('transactions.columns.paymentStatus'),
      width: '10%',
      key: 'type',
      sortable: false,
      rendered: item => (
        <span
          className={`text-xs font-regular ${
            item.paymentStatus === 'paid'
              ? 'bg-green-100 text-green-700'
              : item.paymentStatus === 'failure'
              ? 'bg-red-100 text-red-700'
              : item.paymentStatus === 'unpaid'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-gray-200'
          } px-2 py-1 rounded-md`}
        >
          {t(`transactions.status.${item.paymentStatus}`)}
        </span>
      ),
    },
    {
      name: t('transactions.columns.txtInitiation'),
      width: '5%',
      key: 'transactionDate',
      align: 'right',
      sortable: true,
      rendered: item => (
        <div className="text-sm text-gray-500">
          <Tooltip title={moment.parseZone(item.transactionDate).format('MMMM Do YYYY, h:mm A')}>{moment.parseZone(item.transactionDate).fromNow()}</Tooltip>
        </div>
      ),
    },
    {
      name: t('transactions.columns.txtCompletion'),
      width: '5%',
      key: 'completedAt',
      align: 'right',
      sortable: true,
      rendered: item =>
        item.completedAt && (
          <div className="text-sm text-gray-500">
            <Tooltip title={moment.parseZone(item.completedAt).format('MMMM Do YYYY, h:mm A')}>{moment.parseZone(item.completedAt).fromNow()}</Tooltip>
          </div>
        ),
    },
    {
      name: t('transactions.columns.txtCancellation'),
      width: '5%',
      key: 'canceledAt',
      align: 'right',
      sortable: true,
      rendered: item =>
        item.canceledAt && (
          <div className="text-sm text-gray-500 flex gap-1">
            <Tooltip title={moment.parseZone(item.canceledAt).format('MMMM Do YYYY, h:mm A')}>{moment.parseZone(item.canceledAt).fromNow()}</Tooltip>
          </div>
        ),
    },
    {
      name: t('transactions.columns.action'),
      width: '10%',
      key: 'action',
      align: 'left',
      rendered: item => (
        <div className="flex space-x-2">
          <button onClick={() => handleStatusClick(item)} className="px-2 py-1 border rounded-md shadow-sm text-xs text-white bg-dark-600 hover:bg-dark-700">
            {t('transactions.buttons.editStatus')}
          </button>
          {item.transactionLogs && item.transactionLogs.length > 0 && (
            <button
              onClick={() => {
                setTransactionLogs(item.transactionLogs)
                setSelectedBooking(item)
                setIsDrawerOpen(true)
              }}
              className="px-2 py-1 border rounded-md shadow-sm text-xs text-white bg-dark-600 hover:bg-dark-700"
            >
              {t('transactions.buttons.viewLogs')}
            </button>
          )}
        </div>
      ),
    },
  ]

  const closeDrawer = () => {
    setIsDrawerOpen(false)
    setSelectedBooking(null)
  }

  return (
    <>
      <WithReports
        {...props}
        apiURL="transactions"
        defaultFileName="Transaction"
        forwardedRef={ref}
        columns={columns}
        activeFilterName="past_7_days"
        paymentMethod={paymentMethod}
        status={status}
      />
      <TransactionLogsDrawer isDrawerOpen={isDrawerOpen} closeDrawer={closeDrawer} selectedBooking={selectedBooking} isTransactionDrawer transactionLogs={transactionLogs} />
      <UpdateStatusModal />
    </>
  )
})

export default TransactionLogs
